<!--
Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
Unauthorized use, copying, distribution, displaying, or public performance
of this file, via any medium, is strictly prohibited. All information
contained herein is proprietary and confidential and may not be shared
with any third party without the express written consent of Reciprocity, Inc.
-->

<template>
  <div v-if="serverData" class="wrapper" style="min-height: calc(100vh - 3rem)">
    <h1 class="title">
      <b-button class="back" variant="link" @click="goBack">
        <i class="fa fa-arrow-left" />
        Back </b-button
      >Send questionnaire for:
      <span class="font-weight-bold">{{ serverData.objectTitle }}</span>
      <help-link />
    </h1>
    <div class="frame">
      <wizard-progress :steps="stepsLabels" :current-step="currentStep" />
      <div class="content">
        <div v-if="loading" class="loader-wrapper">
          <div class="loader" />
          Sending your emails, please hold.
        </div>
        <survey-selection
          v-else-if="currentStep === 0"
          v-model="selectedSurveyId"
          :surveys="serverData.surveys"
          @update-validity="setValidity(0, $event)"
        />
        <recipient-details
          v-else-if="currentStep === 1"
          ref="recipientDetails"
          v-model="formData"
          :attempted="stepAttempt[1]"
          :users="serverData.users"
          @update-validity="setValidity(1, $event)"
          @update-email-data="updateEmailMessage($event)"
        />
        <review
          v-else-if="currentStep === 2"
          :survey="selectedSurvey"
          :form-data="formData"
          :email-preview-url-base="serverData.previewEmailUrl"
        />
      </div>
      <div class="button-wrapper">
        <b-button :disabled="currentStep === 0" variant="outline-primary" @click="moveStep('back')">
          Previous
        </b-button>
        <b-button :disabled="loading || currentStep === stepsCount" variant="primary" @click="moveStep('forward')">
          {{ currentStep >= stepsCount - 1 ? 'Submit' : 'Next' }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep, find } from 'lodash';
import { BButton } from 'bootstrap-vue';
import { mapGetters } from 'vuex';

import HelpLink from '@/components/help_link/HelpLink';

import RecipientDetails from './pages/2_recipient_details/2-RecipientDetails.vue';
import Review from './pages/3-Review.vue';
import SurveySelection from './pages/1-SurveySelection.vue';
import WizardProgress from './components/WizardProgress.vue';
import surveyPageMethods from './surveyPageMethods';

export default {
  name: 'Wizard',
  components: {
    BButton,
    RecipientDetails,
    Review,
    SurveySelection,
    WizardProgress,
    HelpLink,
  },
  data() {
    const objectType = this.$route.params.objectType;
    return {
      currentStep: 0,
      selectedSurveyId: null,
      stepValidity: [false, false, true],
      stepAttempt: [false, false, false],
      serverData: null,
      loading: true,
      objectType,
      formData: {
        recipients: [],
        ccs: [],
        notifyOnResponse: false,
        autoNagDate: '',
        autoNagRecurrence: 0,
        message: '',
        greeting: '',
        salutations: '',
        contactEmail: '',
        title: `${objectType} Assessment Questionnaire`,
        internal: false,
      },
      stepsLabels: ['Select questionnaire', 'Recipient details', 'Review'],
    };
  },
  computed: {
    ...mapGetters(['routes', 'featureFlags']),
    selectedSurvey() {
      return find(this.serverData.surveys, { id: this.selectedSurveyId });
    },
    stepsCount() {
      return this.stepsLabels.length;
    },
  },
  created() {
    this.setPageTitle({ title: 'Send Questionnaire', visible: false });

    if (!this.featureFlags.questionnaire_improvements_external_recipients) {
      this.formData.recipients = [
        {
          name: '',
          email: '',
        },
      ];
    }

    this.request({
      route: 'surveySendingGet',
      params: {
        objectId: this.$route.params.objectId,
        objectType: this.$route.params.objectType,
      },
    }).then(response => {
      this.loading = false;
      this.serverData = response;
      this.generateEmailMessage();
    });
  },
  methods: {
    ...surveyPageMethods,
    submit() {
      const formData = cloneDeep(this.formData);
      formData.recipients.forEach(recipient => {
        if (recipient.name === '') {
          recipient.name = null;
        }
      });

      const postData = {
        ...formData,
        objectId: this.$route.params.objectId,
        objectType: this.$route.params.objectType,
        surveyTemplateId: this.selectedSurveyId,
      };

      this.loading = true;
      this.request({
        route: 'surveySendingPost',
        method: 'post',
        data: postData,
        defaultErrorHandler: false,
      })
        .then(response => {
          this.loading = false;
          this.showAlert({
            type: 'success',
            message: 'Questionnaires successfully sent!',
          });
          setTimeout(() => {
            this.redirect(response.redirectUrl);
          }, 1000);
        })
        .catch(error => {
          this.showAlert({
            type: [error.type],
            message: error.message,
          });
          this.loading = false;
          this.moveStep('back');
        });
    },
    generateEmailMessage() {
      this.formData.message =
        `We at ${this.serverData.organization} are ` +
        'evaluating your organization as a prospective business partner. To ' +
        'help us complete our due diligence, please fill out the ' +
        `${this.objectType} assessment questionnaire on behalf ` +
        'of your organization.';
      this.formData.greeting =
        `Thank you!\n- ${this.serverData.productName} ` + `Team at ${this.serverData.organization}`;
      this.formData.contactEmail = this.serverData.contactEmail;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './surveyPage.scss';
</style>
